import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../api";

function LogsModal({ campaign, onClose }) {
    const [allLogs, setAllLogs] = useState([]);
    const [filter, setFilter] = useState("ALL"); 
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLogs = async () => {
            setLoading(true);
            try {
                // Fetch both logs and pending queue items
                const [logsRes, queueRes] = await Promise.all([
                    axios.get(`${API}/sms/logs?campaignId=${campaign._id}&t=${Date.now()}`),
                    axios.get(`${API}/sms/queue?campaignId=${campaign._id}&t=${Date.now()}`)
                ]);

                const logsData = Array.isArray(logsRes.data) ? logsRes.data : [];
                const queueData = Array.isArray(queueRes.data) ? queueRes.data : [];

                // Map queue items to the same format as logs
                const pendingLogs = queueData.map(q => ({
                    ...q,
                    status: "QUEUED", // Visual distinction
                    date: q.sendAt,
                    reason: "Scheduled for later"
                }));

                // Combine and sort by date descending
                const combined = [...logsData, ...pendingLogs].sort((a, b) => new Date(b.date) - new Date(a.date));
                setAllLogs(combined);
            } catch (err) {
                console.error("Error fetching campaign data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, [campaign._id]);

    const filteredLogs = allLogs.filter(l => {
        if (filter === "ALL") return true;
        
        const isSuccess = ["SENT", "DELIVERED", "READ", "SUCCESS", "ACCEPTED"].includes(l.status);
        const isPending = ["PENDING", "PROCESSING", "TRANSMITTED", "QUEUED"].includes(l.status);
        const isFailed = !isSuccess && !isPending;

        if (filter === "SENT") return isSuccess;
        if (filter === "PENDING") return isPending;
        if (filter === "FAILED") return isFailed;
        return true;
    });

    const exportFailed = () => {
        const failedLogs = allLogs.filter(l => !["SENT", "DELIVERED", "READ", "SUCCESS", "TRANSMITTED", "ACCEPTED", "PENDING", "PROCESSING"].includes(l.status));
        if (failedLogs.length === 0) {
            alert("No failed records found to export.");
            return;
        }

        const csvRows = [
            ["Phone", "Reason", "Message"].join(","),
            ...failedLogs.map(l => [l.phone, `"${l.reason || "Unknown"}"`, `"${l.message.replace(/"/g, '""')}"`].join(","))
        ];

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `failed_list_${campaign.name.replace(/\s+/g, '_')}.csv`;
        a.click();
    };

    return (
        <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.85)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000,
            padding: "20px", backdropFilter: "blur(12px)"
        }}>
            <div className="glass-panel" style={{ width: "100%", maxWidth: "1100px", height: "85vh", display: "flex", flexDirection: "column", padding: "40px", overflow: "hidden" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "32px" }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: "1.75rem", fontWeight: "800", color: "#fff" }}>Campaign Logs: {campaign.name}</h2>
                        <div style={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                            <p style={{ color: "var(--text-secondary)", margin: 0 }}>Real-time delivery status for all recipients.</p>
                            <span style={{ color: "var(--accent-primary)", fontSize: "0.85rem", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                                List: {campaign.listName || "All Contacts"}
                            </span>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: "var(--bg-elevated)", border: "1px solid var(--glass-border)", color: "#fff", width: "40px", height: "40px", borderRadius: "12px", fontSize: "1.2rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>&times;</button>
                </div>

                <div style={{ display: "flex", gap: "12px", marginBottom: "32px", alignItems: "center", flexWrap: "wrap" }}>
                    <div className="glass-panel" style={{ display: "flex", padding: "4px", borderRadius: "10px", background: "var(--bg-deep)" }}>
                        <button onClick={() => setFilter("ALL")} style={{ padding: "8px 24px", border: "none", borderRadius: "8px", background: filter === "ALL" ? "var(--bg-elevated)" : "transparent", color: filter === "ALL" ? "#fff" : "var(--text-muted)", cursor: "pointer", fontWeight: "600", fontSize: "0.85rem" }}>All Items</button>
                        <button onClick={() => setFilter("SENT")} style={{ padding: "8px 24px", border: "none", borderRadius: "8px", background: filter === "SENT" ? "var(--bg-elevated)" : "transparent", color: filter === "SENT" ? "#fff" : "var(--text-muted)", cursor: "pointer", fontWeight: "600", fontSize: "0.85rem" }}>Success</button>
                        <button onClick={() => setFilter("PENDING")} style={{ padding: "8px 24px", border: "none", borderRadius: "8px", background: filter === "PENDING" ? "var(--bg-elevated)" : "transparent", color: filter === "PENDING" ? "#fff" : "var(--text-muted)", cursor: "pointer", fontWeight: "600", fontSize: "0.85rem" }}>Pending</button>
                        <button onClick={() => setFilter("FAILED")} style={{ padding: "8px 24px", border: "none", borderRadius: "8px", background: filter === "FAILED" ? "var(--bg-elevated)" : "transparent", color: filter === "FAILED" ? "#fff" : "var(--text-muted)", cursor: "pointer", fontWeight: "600", fontSize: "0.85rem" }}>Failed</button>
                    </div>
                    <div style={{ flex: 1 }}></div>
                    <button onClick={exportFailed} className="btn-primary" style={{ padding: "0 24px", height: "44px", background: "var(--accent-primary)", border: "none" }}>
                        📥 Export CSV
                    </button>
                </div>

                <div className="table-responsive" style={{ flex: 1, border: "1px solid var(--glass-border)", borderRadius: "12px" }}>
                    {loading ? <p style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>Synchronizing logs...</p> : (
                        <table>
                            <thead style={{ position: "sticky", top: 0, zIndex: 10, background: "var(--bg-surface)" }}>
                                <tr>
                                    <th>Recipient</th>
                                    <th>Delivery Status</th>
                                    <th>Reason / Details</th>
                                    <th>Preview</th>
                                    <th>Time</th>
                                    <th style={{ textAlign: "right" }}>Sync</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLogs.length === 0 ? (
                                    <tr><td colSpan="6" style={{ textAlign: "center", padding: "60px", color: "var(--text-muted)" }}>No records found for the selected filter.</td></tr>
                                ) : filteredLogs.map(l => (
                                    <tr key={l._id}>
                                        <td style={{ fontWeight: "700", color: "#fff" }}>{l.phone}</td>
                                        <td>
                                            <span className={`status-badge status-${(l.status || "PENDING").toLowerCase()}`}>{l.status || "PENDING"}</span>
                                        </td>
                                        <td style={{ color: ["SENT", "DELIVERED", "READ", "SUCCESS", "ACCEPTED"].includes(l.status) ? "var(--text-muted)" : l.status === "TRANSMITTED" ? "var(--warning)" : l.status === "PENDING" ? "var(--warning)" : "var(--danger)", fontSize: "0.85rem", fontWeight: "500" }}>
                                            {l.reason || (l.status === "TRANSMITTED" ? "Transmitted to network, awaiting report" : ["SENT", "DELIVERED", "READ", "SUCCESS", "ACCEPTED"].includes(l.status) ? "Successful" : "Pending/Failed")}
                                        </td>
                                        <td style={{ fontSize: "0.8rem", color: "var(--text-muted)", opacity: 0.8 }}>
                                            {l.message.substring(0, 40)}...
                                        </td>
                                        <td style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                                            {new Date(l.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            {l.messageId && (
                                                <button 
                                                    onClick={async (e) => {
                                                        const btn = e.currentTarget;
                                                        btn.disabled = true;
                                                        btn.innerText = "...";
                                                        try {
                                                            const res = await axios.get(`${API}/sms/sync/${l.messageId}`);
                                                            if (res.data.success) {
                                                                // Update local state instead of reloading
                                                                setAllLogs(prev => prev.map(item => 
                                                                    item._id === l._id ? { ...item, status: res.data.status, reason: res.data.reason } : item
                                                                ));
                                                            } else {
                                                                alert(res.data.message);
                                                            }
                                                        } catch (err) {
                                                            alert("Sync failed: " + err.message);
                                                        } finally {
                                                            btn.disabled = false;
                                                            btn.innerText = "Sync";
                                                        }
                                                    }}
                                                    style={{
                                                        padding: "4px 10px", fontSize: "0.7rem", borderRadius: "6px",
                                                        background: "var(--bg-elevated)", color: "#fff", border: "1px solid var(--glass-border)",
                                                        cursor: "pointer"
                                                    }}
                                                >Sync</button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function ActiveCampaigns() {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusUpdating, setStatusUpdating] = useState(null);
    const [selectedCampaign, setSelectedCampaign] = useState(null);

    const fetchCampaigns = async (isFirstLoad = false) => {
        if (isFirstLoad) setLoading(true);
        try {
            const res = await axios.get(`${API}/sms/campaigns?_t=${Date.now()}`, {
                skipLoader: !isFirstLoad,
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate'
                }
            });
            setCampaigns(res.data);
        } catch (error) {
            console.error("Error fetching campaigns:", error);
        } finally {
            if (isFirstLoad) setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns(true);
        const interval = setInterval(() => fetchCampaigns(false), 3000);
        return () => clearInterval(interval);
    }, []);

    const updateStatus = async (id, status) => {
        setStatusUpdating(id);
        try {
            await axios.post(`${API}/campaigns/${id}/status`, { status });
            setStatusUpdating(null);
            fetchCampaigns(false);
        } catch (error) {
            alert("Error updating status: " + (error.response?.data?.message || error.message));
            setStatusUpdating(null);
        }
    };

    const deleteCampaign = async (id) => {
        if (!window.confirm("Are you sure? This will delete all pending messages in this campaign.")) return;
        try {
            await axios.delete(`${API}/campaigns/${id}`);
            alert("Campaign deleted.");
            fetchCampaigns(false);
        } catch (error) {
            alert("Error deleting campaign: " + (error.response?.data?.message || error.message));
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "RUNNING": return "var(--success)";
            case "QUEUED": return "var(--warning)";
            case "PAUSED": return "#fbbf24";
            case "COMPLETED": return "var(--accent-primary)";
            case "CANCELED": return "var(--danger)";
            default: return "var(--text-secondary)";
        }
    };

    return (
        <div className="page-container">
            {selectedCampaign && <LogsModal campaign={selectedCampaign} onClose={() => setSelectedCampaign(null)} />}
            
            <div className="header-actions" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "32px" }}>
                <div>
                    <h1>Campaign Management</h1>
                    <p style={{ color: "var(--text-secondary)", margin: 0 }}>Monitor real-time progress and manage active broadcasts.</p>
                </div>
                <div className="header-btns" style={{ display: "flex", gap: "12px" }}>
                    <button 
                        onClick={async (e) => {
                            const btn = e.currentTarget;
                            btn.disabled = true;
                            const oldText = btn.innerText;
                            btn.innerText = "Syncing...";
                            try {
                                const res = await axios.get(`${API}/sms/sync-all`);
                                alert(res.data.message);
                                fetchCampaigns(false);
                            } catch (err) {
                                alert("Sync failed: " + err.message);
                            } finally {
                                btn.disabled = false;
                                btn.innerText = oldText;
                            }
                        }}
                        className="glass-input" style={{ width: "auto", height: "40px", cursor: "pointer", fontSize: "0.85rem", fontWeight: "600" }}
                    >
                        🔄 Sync All Status
                    </button>
                    <button onClick={() => fetchCampaigns(false)} className="glass-input" style={{ width: "auto", height: "40px", cursor: "pointer", fontSize: "0.85rem", fontWeight: "600" }}>
                        🔄 Refresh Data
                    </button>
                </div>
            </div>

            <div className="glass-panel" style={{ padding: "0" }}>
                {loading ? <p style={{ padding: "40px", textAlign: "center" }}>Collecting campaign data...</p> : (
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>Campaign</th>
                                    <th>Status</th>
                                    <th style={{ width: "25%" }}>Progress</th>
                                    <th>Delivery Stats</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.length === 0 ? (
                                    <tr><td colSpan="5" style={{ textAlign: "center", padding: "60px", color: "var(--text-muted)" }}>No campaigns found. Start one to see it here.</td></tr>
                                ) : campaigns.map(cam => {
                                    const total = cam.totalMessages || 0;
                                    const sent = cam.totalSent || 0;
                                    const failed = cam.failedCount || 0;
                                    const transmitted = cam.transmittedCount || 0;
                                    const processed = sent + failed + transmitted;
                                    const percent = total > 0 ? Math.round((processed / total) * 100) : 0;

                                    return (
                                        <tr key={cam._id}>
                                            <td style={{ padding: "20px 16px" }}>
                                                <div style={{ fontWeight: "700", color: "#fff", marginBottom: "4px" }}>{cam.name}</div>
                                                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                                    {new Date(cam.date).toLocaleString()}
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: getStatusColor(cam.status) }}></div>
                                                    <span style={{ fontSize: "0.85rem", fontWeight: "600", color: getStatusColor(cam.status) }}>{cam.status}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                                    <div style={{ width: "100%", background: "var(--bg-deep)", height: "6px", borderRadius: "3px", overflow: "hidden" }}>
                                                        <div style={{ width: `${percent}%`, height: "100%", background: getStatusColor(cam.status), transition: "width 1s ease" }}></div>
                                                    </div>
                                                    <span style={{ fontSize: "0.75rem", fontWeight: "700", color: "var(--text-secondary)" }}>{percent}% Complete</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                                                    <div>
                                                        <div style={{ fontSize: "1.1rem", fontWeight: "700", color: "var(--success)" }}>{sent}</div>
                                                        <div style={{ fontSize: "0.65rem", textTransform: "uppercase", color: "var(--text-muted)" }}>Sent</div>
                                                    </div>
                                                    <div style={{ width: "1px", height: "20px", background: "var(--glass-border)" }}></div>
                                                    <div>
                                                        <div style={{ fontSize: "1.1rem", fontWeight: "700", color: "var(--warning)" }}>{transmitted}</div>
                                                        <div style={{ fontSize: "0.65rem", textTransform: "uppercase", color: "var(--text-muted)" }}>Pending</div>
                                                    </div>
                                                    <div style={{ width: "1px", height: "20px", background: "var(--glass-border)" }}></div>
                                                    <div>
                                                        <div style={{ fontSize: "1.1rem", fontWeight: "700", color: "var(--danger)" }}>{failed}</div>
                                                        <div style={{ fontSize: "0.65rem", textTransform: "uppercase", color: "var(--text-muted)" }}>Failed</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                                                    <button onClick={() => setSelectedCampaign(cam)} className="glass-input" style={{ width: "auto", height: "36px", cursor: "pointer", fontSize: "0.8rem", padding: "0 12px" }}>
                                                        View Logs
                                                    </button>
                                                    {["RUNNING", "QUEUED"].includes(cam.status) ? (
                                                        <button onClick={() => updateStatus(cam._id, "PAUSED")} className="glass-input" style={{ width: "auto", height: "36px", padding: "0 12px", background: "rgba(245, 158, 11, 0.1)", color: "var(--warning)", border: "1px solid rgba(245, 158, 11, 0.2)" }}>Pause</button>
                                                    ) : cam.status === "PAUSED" ? (
                                                        <button onClick={() => updateStatus(cam._id, "RUNNING")} className="glass-input" style={{ width: "auto", height: "36px", padding: "0 12px", background: "rgba(16, 185, 129, 0.1)", color: "var(--success)", border: "1px solid rgba(16, 185, 129, 0.2)" }}>Resume</button>
                                                    ) : null}
                                                    <button onClick={() => deleteCampaign(cam._id)} style={{ width: "36px", height: "36px", borderRadius: "8px", border: "1px solid rgba(239, 68, 68, 0.2)", background: "rgba(239, 68, 68, 0.1)", color: "var(--danger)", cursor: "pointer" }}>&times;</button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

